import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { closeSearchModal, openOrFocusTicketTab } from '../../../../actions/ui';
import { SearchResultsList } from './components';
import { useGlobalSearch } from './hooks';

const FILTER_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'customer', label: 'Clientes' },
    { value: 'project', label: 'Proyectos' },
    { value: 'user', label: 'Usuarios' },
];

export const SearchResultsScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);
    const [searchParams, setSearchParams] = useSearchParams();

    const queryParam = String(searchParams.get('q') || '');
    const typeParam = String(searchParams.get('type') || 'all').toLowerCase();

    const {
        query,
        setQuery,
        activeFilter,
        setActiveFilter,
        results,
        loading,
        error,
        totalCount,
    } = useGlobalSearch({
        token,
        initialQuery: queryParam,
        initialFilter: typeParam,
        limit: 25,
    });

    React.useEffect(() => {
        if (query !== queryParam) {
            setQuery(queryParam);
        }
    }, [query, queryParam, setQuery]);

    React.useEffect(() => {
        if (activeFilter !== typeParam) {
            setActiveFilter(typeParam);
        }
    }, [activeFilter, typeParam, setActiveFilter]);

    React.useEffect(() => {
        const nextQuery = String(query || '').trim();
        const nextType = activeFilter || 'all';
        const currentQuery = String(searchParams.get('q') || '');
        const currentType = String(searchParams.get('type') || 'all').toLowerCase();

        if (nextQuery === currentQuery && nextType === currentType) {
            return;
        }

        const nextParams = {};
        if (nextQuery) {
            nextParams.q = nextQuery;
        }
        nextParams.type = nextType;
        setSearchParams(nextParams, { replace: true });
    }, [query, activeFilter, searchParams, setSearchParams]);

    const handleSelectTicket = React.useCallback((ticket) => {
        if (!ticket?._id) {
            return;
        }

        dispatch(openOrFocusTicketTab({
            id: ticket._id,
            code: ticket.code,
            title: ticket.title,
        }));
        dispatch(closeSearchModal());
        navigate(`/tasks/detail/${ticket._id}`, {
            state: { ticketCode: ticket.code },
        });
    }, [dispatch, navigate]);

    const handleSelectCustomer = React.useCallback((customer) => {
        if (!customer?._id) {
            return;
        }

        dispatch(closeSearchModal());
        navigate(`/customer/detail/${customer._id}`);
    }, [dispatch, navigate]);

    const handleSelectProject = React.useCallback((project) => {
        if (!project?._id) {
            return;
        }

        dispatch(closeSearchModal());
        navigate(`/project/detail/${project._id}`);
    }, [dispatch, navigate]);

    const handleSelectUser = React.useCallback((user) => {
        if (!user?._id) {
            return;
        }

        dispatch(closeSearchModal());
        navigate('/settings/users', {
            state: {
                highlightUserId: user._id,
            },
        });
    }, [dispatch, navigate]);

    return (
        <div className="row px-2 px-md-3 py-3">
            <div className="col-12 col-xl-10 col-xxl-8 mx-auto">
                <div className="card card__no-border search-results-screen">
                    <div className="card-body p-3 p-md-4">
                        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                            <div>
                                <h4 className="fw-bold mb-1">Búsqueda global</h4>
                                <p className="text-muted mb-0">Busca tickets, clientes, proyectos y usuarios dentro de tu compañía.</p>
                            </div>
                            <div className="search-results-screen__summary">
                                {totalCount} resultado{totalCount === 1 ? '' : 's'}
                            </div>
                        </div>

                        <div className="search-modal__search-shell search-modal__search-shell--page mb-3">
                            <i className="feather-search search-modal__search-icon" aria-hidden="true" />
                            <input
                                type="text"
                                className="search-modal__input"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Buscar por código, nombre, correo o título..."
                            />
                        </div>

                        <div className="search-modal__chips mb-3">
                            {FILTER_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`search-modal__chip ${activeFilter === option.value ? 'is-active' : ''}`}
                                    onClick={() => setActiveFilter(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        <SearchResultsList
                            query={query}
                            loading={loading}
                            error={error}
                            results={results}
                            totalCount={totalCount}
                            onSelectTicket={handleSelectTicket}
                            onSelectCustomer={handleSelectCustomer}
                            onSelectProject={handleSelectProject}
                            onSelectUser={handleSelectUser}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
